import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorColoringOptions,
	CreatorOptions,
	CreatorRendererOptions,
	CreatorState,
	CreatorStep,
	HistoryAction,
	RoomDragType,
} from '@/types/creator';
import { ThreeEvent, ThreeEvents } from '@/renderer/types';
import { CreatorProductModule } from '@/api/CreatorProductModuleApi';
import { UUID } from '@/types/types';
import { Project, ProjectContact } from '@/api/ProjectApi';
import { CreatorLandscapeModule } from '@/api/CreatorLandscapeModuleApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Preset } from '@/api/PresetApi';
import { computeLevelsQuantity } from '@/utils/utilities';
import { notifications } from '@mantine/notifications';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import APP_CONFIG from '@/configs/appConfig';

const initialState: CreatorState = {
	creatorProductModules: [],
	creatorLandscapeModules: [],
	creatorProductModulesByRefId: {},
	creatorLandscapeModulesByRefId: {},
	name: '',
	contact: {
		fullName: '',
		email: '',
		phonePrefix: '',
		phoneNumber: '',
		companyName: '',
		vatNumber: '',
		address: '',
		comment: '',
	},
	renderer: {
		zoomLevel: RENDERER_CONFIG.INITIAL_ZOOM,
		loading: false,
		distance: RENDERER_CONFIG.INITIAL_DISTANCE,
		currentLevel: 0,
		levelsQuantity: computeLevelsQuantity(APP_CONFIG.ROOM_HEIGHT.DEFAULT / 100),
	},
	options: {
		coloringOptions: {
			selectedAttributeValue: null,
			popoverOpened: false,
		},
		isPresetMode: false,
		isPreviewMode: false,
		selectedShape: null,
		customBackground: {
			attachment: null,
			dimensions: [0, 0],
			rotation: 0,
		},
		roomHeight: APP_CONFIG.ROOM_HEIGHT.DEFAULT,
		roomDragType: RoomDragType.WALL,
		currentStep: CreatorStep.SHAPES,
		isLoading: false,
		isDrawing: false,
		productModulesDrawerOpened: false,
		infoProductModule: null,
		landscapeModulesDrawerOpened: false,
		successDrawerOpened: false,
		instructionModalOpened: false,
		activeCamera: APP_CONFIG.DEFAULT_CAMERA,
		wallsVisibility: true,
		roomSurfaceArea: 0,
		largestAvailableRectangle: {
			width: 0,
			height: 0,
		},
		productModuleContextMenuData: {
			moduleLevel: 0,
			creatorProductModuleRefId: null,
			position: [0, 0],
		},
		isMeasuring: false,
		creatorMeasurements: [],
		historyLength: 0,
		currentHistoryIndex: -1,
	},
	project: null,
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		loadProject: (state, { payload }: PayloadAction<Project>) => {
			state.name = payload.name;
			state.contact = payload.contact;

			state.creatorProductModules = payload.creatorProductModules.map(
				(creatorProductModule) => ({
					...creatorProductModule,
					id: creatorProductModule.refId,
				})
			);
			state.creatorLandscapeModules = payload.creatorLandscapeModules.map(
				(creatorLandscapeModule) => ({
					...creatorLandscapeModule,
					id: creatorLandscapeModule.refId,
				})
			);
			state.creatorProductModulesByRefId = {};
			state.creatorLandscapeModulesByRefId = {};

			for (const creatorProductModule of state.creatorProductModules)
				state.creatorProductModulesByRefId[creatorProductModule.refId] =
					creatorProductModule;

			for (const creatorLandscapeModule of state.creatorLandscapeModules)
				state.creatorLandscapeModulesByRefId[
					creatorLandscapeModule.refId
				] = creatorLandscapeModule;

			state.options = payload.options.state.options;
			state.options.currentStep = CreatorStep.SHAPES;
			state.options.wallsVisibility = true;
			state.options.activeCamera = APP_CONFIG.DEFAULT_CAMERA;
			state.options.historyLength = 0;
			state.options.currentHistoryIndex = -1;

			state.renderer = payload.options.state.renderer;
			state.renderer.currentLevel = 0;
		},
		loadFromPreset: (state, { payload }: PayloadAction<Preset>) => {
			state.creatorProductModules = payload.creatorProductModules.map(
				(creatorLandscapeModule) => ({
					...creatorLandscapeModule,
					id: creatorLandscapeModule.refId,
				})
			);

			for (const creatorProductModule of state.creatorProductModules)
				state.creatorProductModulesByRefId[creatorProductModule.refId] =
					creatorProductModule;

			state.options.historyLength = 0;
			state.options.currentHistoryIndex = -1;
		},
		copyProject: (state) => {
			state.options.isPreviewMode = false;
		},
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateContact: (
			state,
			{ payload }: PayloadAction<Partial<ProjectContact>>
		) => {
			Object.assign(state.contact, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.MOUSE_OVER_MODULE:
					for (const object of [
						...state.creatorLandscapeModules,
						...state.creatorProductModules,
					]) {
						object.creatorOptions.isMouseOver =
							(payload.data as ThreeEvents[ThreeEvent.MOUSE_OVER_MODULE]) ===
							object.refId;
					}
					break;
				case ThreeEvent.UPDATE_OPTIONS:
					Object.assign(state.options, payload.data);
					break;
				case ThreeEvent.ZOOM_LEVEL:
					state.renderer.zoomLevel =
						payload.data as ThreeEvents[ThreeEvent.ZOOM_LEVEL];
					break;
				case ThreeEvent.DISTANCE:
					state.renderer.distance =
						payload.data as ThreeEvents[ThreeEvent.DISTANCE];
					break;
				case ThreeEvent.CONTEXT_MENU: {
					const data = payload.data as ThreeEvents[ThreeEvent.CONTEXT_MENU];

					state.options.productModuleContextMenuData.creatorProductModuleRefId =
						data.id;
					state.options.productModuleContextMenuData.position = data.position;
					state.options.productModuleContextMenuData.moduleLevel =
						data.moduleLevel;
					break;
				}
				case ThreeEvent.SET_MEASUREMENTS:
					state.options.creatorMeasurements =
						payload.data as ThreeEvents[ThreeEvent.SET_MEASUREMENTS];
					break;
				case ThreeEvent.MODULES_HISTORY:
					Object.assign(state.options, payload.data);
					break;
				case ThreeEvent.MODULES_RESTORED: {
					const snapshot =
						payload.data as ThreeEvents[ThreeEvent.MODULES_RESTORED];

					state.creatorProductModules = snapshot.productModules.flatMap(
						({ id }) => {
							const creatorProductModule =
								state.creatorProductModulesByRefId[id];
							if (!creatorProductModule) return [];

							creatorProductModule.creatorOptions.isMouseOver = false;
							return [creatorProductModule];
						}
					);
					state.creatorLandscapeModules = snapshot.landscapeModules.flatMap(
						({ id }) => {
							const creatorLandscapeModule =
								state.creatorLandscapeModulesByRefId[id];
							if (!creatorLandscapeModule) return [];

							creatorLandscapeModule.creatorOptions.isMouseOver = false;
							return [creatorLandscapeModule];
						}
					);
					state.options.productModuleContextMenuData.creatorProductModuleRefId =
						null;
					break;
				}
				case ThreeEvent.DETECTED_COLLISION:
					notifications.show({
						color: 'red',
						title: 'Kolizja przeszkód!',
						message:
							'Obrót przeszkody został zablokowany z powodu kolizji z inną przeszkodą.',
					});
					break;
				default:
					throw new Error('UNKNOW_EVENT');
			}
		},
		setCreatorStep: (state, { payload }: PayloadAction<'prev' | 'next'>) => {
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;
			state.options.productModuleContextMenuData.creatorProductModuleRefId =
				null;
			state.options.isMeasuring = false;
		},
		selectShape: (
			state,
			{ payload }: PayloadAction<CreatorOptions['selectedShape']>
		) => {
			state.options.selectedShape = payload;
		},
		setActiveCamera: (
			state,
			{ payload }: PayloadAction<CreatorOptions['activeCamera']>
		) => {
			state.options.isMeasuring = false;
			state.options.activeCamera = payload;
		},
		setRoomDragType: (
			state,
			{ payload }: PayloadAction<CreatorOptions['roomDragType']>
		) => {
			state.options.roomDragType = payload;
		},
		addCreatorProductModule: (
			state,
			{ payload }: PayloadAction<CreatorProductModule>
		) => {
			state.creatorProductModules.push(payload);
			state.creatorProductModulesByRefId[payload.refId] = payload;
			state.options.productModuleContextMenuData.creatorProductModuleRefId =
				null;
		},
		removeCreatorProductModule: (state, { payload }: PayloadAction<UUID>) => {
			state.creatorProductModules = state.creatorProductModules.filter(
				(item) => item.refId !== payload
			);

			if (!state.creatorProductModules.length) {
				state.options.currentHistoryIndex = -1;
				state.options.historyLength = 0;
			}
		},
		addCreatorLandscapeModule: (
			state,
			{ payload }: PayloadAction<CreatorLandscapeModule>
		) => {
			state.creatorLandscapeModules.push(payload);
			state.creatorLandscapeModulesByRefId[payload.refId] = payload;
			state.options.landscapeModulesDrawerOpened = false;
		},
		removeCreatorLandscapeModule: (state, { payload }: PayloadAction<UUID>) => {
			state.creatorLandscapeModules = state.creatorLandscapeModules.filter(
				(item) => item.refId !== payload
			);
		},
		setRoomHeight: (state, { payload }: PayloadAction<number>) => {
			state.options.roomHeight = payload;
		},
		setWallsVisibility: (
			state,
			{ payload }: PayloadAction<CreatorOptions['wallsVisibility']>
		) => {
			state.options.wallsVisibility = payload;
		},
		setCurrentLevel: (state, { payload }: PayloadAction<number>) => {
			state.renderer.currentLevel = payload;
		},
		enterPresetMode: (state, { payload }: PayloadAction<Preset | null>) => {
			state.options.isPresetMode = true;
			state.options.currentStep = CreatorStep.MODULES;
			state.options.historyLength = 0;
			state.options.currentHistoryIndex = -1;

			if (!payload) return;

			state.creatorProductModules = payload.creatorProductModules.map(
				(creatorProductModule) => ({
					...creatorProductModule,
					id: creatorProductModule.refId,
				})
			);

			for (const creatorProductModule of state.creatorProductModules)
				state.creatorProductModulesByRefId[creatorProductModule.refId] =
					creatorProductModule;
		},
		generateRandomAttributeValues: (
			state,
			_action: PayloadAction<{
				attributeValues: AttributeValue[];
			}>
		) => {},
		updateColoringOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorColoringOptions>>
		) => {
			Object.assign(state.options.coloringOptions, payload);
		},
		handleHistoryAction: (
			state,
			action: PayloadAction<{
				action: HistoryAction;
			}>
		) => {},
		setMeasuring: (state, { payload }: PayloadAction<boolean>) => {
			if (state.options.isMeasuring === payload) return;
			state.options.isMeasuring = payload;
		},
		removeMeasurement: (state, { payload }: PayloadAction<UUID>) => {
			state.options.creatorMeasurements =
				state.options.creatorMeasurements.filter((item) => item.id !== payload);
		},
		resetCreator: (state) => {},
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
